// ---------------------------------------------------------------------------
// Null-safe group-by
// ---------------------------------------------------------------------------
//
// Perspective renders a group whose key is `null` as a NON-expandable leaf
// (no +/- toggle, link-styled) even though it aggregates correctly — the leaf
// behaviour is inherent to a null group KEY and cannot be fixed by relabelling
// the cell text. The fix is to group by a coalescing EXPRESSION column instead
// of the raw column, so the group key is never null.
//
// The expression maps `null` to a fixed ASCII SENTINEL. The sentinel is ASCII
// — NOT the human label — because Perspective's ExprTK tokenizer rejects
// non-ASCII string literals ("Invalid string token" on the first Cyrillic
// byte, verified against @perspective-dev at runtime). The grid relabels the
// sentinel to the human `NO_GROUP_LABEL` for display, and the export strips the
// expression entirely so it groups by the raw column (whose null key the
// export already handles via `__ROW_PATH__`).
//
// Only STRING group columns are coalesced — coalescing an integer/float/date
// column to a string would change its type and break aggregation/formatting.

import { NO_GROUP_LABEL } from './labels'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Sort = readonly [string, string]

/**
 * Perspective's `Expressions` type is `{ [k]: string | undefined }`; the value
 * may be optional, so both inputs accept the optional form.
 */
type ExpressionMap = Readonly<Record<string, string | undefined>>

type GroupByConfig = {
  readonly group_by?: readonly string[]
  readonly columns?: readonly (string | null)[]
  readonly sort?: readonly Sort[]
  readonly aggregates?: Readonly<Record<string, string | undefined>>
  readonly expressions?: ExpressionMap
}

export type NullSafeGroupBy = {
  readonly group_by: string[]
  readonly expressions: Record<string, string>
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Prefix marking an expression column generated by `nullSafeGroupBy`. */
export const NG_PREFIX = '__ng__'

/**
 * ASCII placeholder the coalescing expression emits for a null group key.
 * Must be ASCII (ExprTK rejects non-ASCII string literals) and improbable as a
 * real data value. The grid swaps it for `NO_GROUP_LABEL` at display time.
 */
export const NO_GROUP_SENTINEL = '__NO_GROUP__'

const STRING_TYPES: ReadonlySet<string> = new Set(['string'])

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

/** Expression column name for a raw group field, e.g. `category` -> `__ng__category`. */
const ngName = (field: string): string => `${NG_PREFIX}${field}`

/** Raw field name behind an expression column name, or the name unchanged. */
const rawName = (name: string): string =>
  name.startsWith(NG_PREFIX) ? name.slice(NG_PREFIX.length) : name

/** Coalescing expression body: null key -> ASCII sentinel, else the raw value. */
const ngBody = (field: string): string =>
  `if (is_null("${field}")) {'${NO_GROUP_SENTINEL}'} else {"${field}"}`

const isStringColumn = (
  field: string,
  columnTypes: Readonly<Record<string, string>>
): boolean => STRING_TYPES.has(columnTypes[field] ?? '')

// ---------------------------------------------------------------------------
// Live-grid transform
// ---------------------------------------------------------------------------

/**
 * Rewrites a group-by list so each STRING group column is grouped by a
 * null-coalescing expression instead of the raw column, and returns the
 * expressions those names require (merged with any pre-existing expressions).
 *
 * Non-string group columns and value columns are left untouched, since
 * coalescing them to a string would break their type.
 */
export const nullSafeGroupBy = (
  groupBy: readonly string[],
  columnTypes: Readonly<Record<string, string>>,
  existingExpressions: ExpressionMap = {}
): NullSafeGroupBy => {
  const stringFields = groupBy.filter((field) =>
    isStringColumn(field, columnTypes)
  )

  const generated = Object.fromEntries(
    stringFields.map((field) => [ngName(field), ngBody(field)])
  )

  const kept = Object.fromEntries(
    Object.entries(existingExpressions).filter(
      (entry): entry is [string, string] => entry[1] !== undefined
    )
  )

  const group_by = groupBy.map((field) =>
    isStringColumn(field, columnTypes) ? ngName(field) : field
  )

  return {
    group_by,
    expressions: { ...kept, ...generated }
  }
}

// ---------------------------------------------------------------------------
// Header display
// ---------------------------------------------------------------------------

/**
 * Maps a rendered group-header value back to what the user should see:
 * the ASCII sentinel becomes the human null-group label, and an `__ng__<field>`
 * expression name becomes the raw field name (so the caller can resolve its
 * display name). Any other value passes through unchanged.
 */
export const displayGroupValue = (value: string): string => {
  if (value === NO_GROUP_SENTINEL) {
    return NO_GROUP_LABEL
  }

  return rawName(value)
}

// ---------------------------------------------------------------------------
// Export decoupling
// ---------------------------------------------------------------------------

/**
 * Strips every `__ng__*` expression from a view config and maps any
 * `__ng__<field>` reference back to the raw `<field>` across `group_by`,
 * `columns`, `sort`, and `aggregates`. The export then groups by the RAW
 * columns exactly as before — its null group key handling (via `__ROW_PATH__`)
 * stays correct and never sees the generated expressions.
 */
export const stripNullSafeGroupBy = <T extends GroupByConfig>(config: T): T => {
  const expressions = Object.fromEntries(
    Object.entries(config.expressions ?? {}).filter(
      ([name]) => !name.startsWith(NG_PREFIX)
    )
  )

  const group_by = (config.group_by ?? []).map(rawName)

  const columns = config.columns?.filter(
    (column) => !(typeof column === 'string' && column.startsWith(NG_PREFIX))
  )

  const sort = config.sort?.map(([column, dir]): Sort => [rawName(column), dir])

  const aggregates = config.aggregates
    ? Object.fromEntries(
        Object.entries(config.aggregates).filter(
          ([column]) => !column.startsWith(NG_PREFIX)
        )
      )
    : config.aggregates

  return {
    ...config,
    group_by,
    columns,
    sort,
    aggregates,
    expressions
  }
}
